import { useQuery, UseQueryResult } from '@tanstack/react-query';
import siteBatchService from '../../../services/SiteBatchService';
import { RuntimeService } from '../../../services/RuntimeService';

/**
 * One available image version for a canonical service.
 * `sha` is populated from the GitHub tag's commit pointer — useful for
 * audit links but not required for dispatch.
 */
export interface ServiceVersion {
  name: string;
  sha?: string;
}

interface UseServiceVersionsOptions {
  /**
   * Gate the query so we don't spam the backend — enable only when the
   * row actually needs version data (row in reuse mode, or user hovers
   * the reuse radio). Matches the design's "lazy per-row fetch" rule:
   * 8 rows don't all hit GitHub up front.
   */
  enabled: boolean;
  /** Upper bound for returned versions. Backend clamps to [1, 100]. */
  limit?: number;
  /**
   * git: GitHub 태그(사내 재사용 배포 — 사내 서버에서 재태그). company: 사내 서버에 실제로
   * 있는 이미지 버전(현장 통합서버 승격은 그 이미지를 보내므로 여기 있는 것만 된다).
   */
  source?: 'git' | 'company';
}

export const serviceVersionsQueryKey = (
  projectId: string | null,
  appname: string,
  source: 'git' | 'company' = 'git',
) => ['service-versions', projectId, appname, source] as const;

/**
 * `useServiceVersions(projectId, appname, { enabled })` — GitHub tag list
 * for the deploy dialog's reuse-mode dropdown.
 *
 * - **Realtime**: staleTime=0 so each modal open refetches. The user
 *   expects the dropdown to show versions they just pushed.
 * - **Retries**: one retry on network/server errors. Beyond that the UI
 *   shows a "재시도" button via `refetch()`.
 * - **Cache lifetime**: short (60s) because versions change rarely but
 *   the dropdown is ephemeral (only visible while dialog is open).
 */
export function useServiceVersions(
  projectId: string | null,
  appname: string,
  options: UseServiceVersionsOptions,
): UseQueryResult<ServiceVersion[], Error> {
  const { enabled, limit = 20, source = 'git' } = options;
  return useQuery({
    queryKey: serviceVersionsQueryKey(projectId, appname, source),
    queryFn: async ({ signal }) => {
      if (!projectId) {
        return [];
      }
      if (source === 'company') {
        return (await RuntimeService.getImageVersions(appname, signal)).map((name) => ({ name }));
      }
      return siteBatchService.listServiceVersions(projectId, appname, limit);
    },
    enabled: enabled && !!projectId && !!appname,
    staleTime: 0,
    gcTime: 60_000,
    retry: 1,
  });
}
